import { useSelector } from "react-redux";
import {
  useAssignApplicationMutation,
  useUnassignApplicationMutation,
  useReportConflictMutation,
  useRestartEvaluationMutation,
  useSaveReviewMutation,
  useNotifyAdminMutation,
  useClearNotifyAdminMutation,
  useReassignApplicationMutation,
  useCreateTrainingMutation,
  useUpdatePriorityMutation,
  useUnassignTraineeMutation,
  useEditTrainingMutation,
} from "../../../../services/graphql/api";
import { assignApplication as assignApplicationAction } from "../../../../services/graphql/slices/manageApplicationsSlice";
import { reassignApplication as reassignApplicationAction } from "../../../../services/graphql/slices/manageApplicationsSlice";
import { unassignApplication as unassignApplicationAction } from "../../../../services/graphql/slices/manageApplicationsSlice";
import { notifyAdmin as notifyAdminAction, clearNotifyAdmin as clearNotifyAdminAction, createTraining as createTrainingAction, restartApplication as restartApplicationAction, saveReview as saveReviewAction, updatePriority as updatePriorityAction, unassignTrainee as unassignTraineeAction, editTraining as editTrainingAction } from "../../../../services/graphql/slices/applicantActionsSlice";
import { reportConflict as reportConflictAction } from "../../../../services/graphql/slices/reportsSlice";
import { exportStringArray } from "../../../NotesState";
import { ERROR_CONSTS, SEVERITY_LEVELS, STATUS } from "../../../../util/constants";
import { isValidFutureDate } from "../../../../util/functions";
import { useDispatch } from "react-redux";

import {
  SAVE_REVIEW,
  ASSIGN_APPLICATION,
  UNASSIGN_APPLICATION,
  RESTART_APPLICATION,
  CLEAR_NOTIFY_ADMIN,
  NOTIFY_ADMIN,
  REASSIGN_APPLICATION,
  REPORT_CONFLICT,
  OVERRIDE_RECOMMENDATION,
  CREATE_TRAINING,
  ASSIGN_TRAINEE,
  UPDATE_PRIORITY,
  UNASSIGN_TRAINEE,
  EDIT_TRAINING,
} from '../../../../actions-index';
import { getStudentState, getTrainingState, getUnassignTraineeState, getEditTrainingState } from "../../../../Common/redux/selectors";
import { useLocation } from "react-router-dom";


const useActionsDialog = ({ type, pageAppHeader, onClose, reviewer, secondReviewer, completedByDate, dialogState, data }) => {

  const dispatch = useDispatch();
  const location = useLocation();
  const isApplicantPage = location?.pathname?.includes("/applicant");

  // RTK Query mutation hooks
  const [assignApplicationMutation] = useAssignApplicationMutation();
  const [unassignApplicationMutation] = useUnassignApplicationMutation();
  const [reportConflictMutation] = useReportConflictMutation();
  const [restartEvaluationMutation] = useRestartEvaluationMutation();
  const [saveReviewMutation] = useSaveReviewMutation();
  const [notifyAdminMutation] = useNotifyAdminMutation();
  const [clearNotifyAdminMutation] = useClearNotifyAdminMutation();
  const [reassignApplicationMutation] = useReassignApplicationMutation();
  const [createTrainingMutation] = useCreateTrainingMutation();
  const [updatePriorityMutation] = useUpdatePriorityMutation();
  const [unassignTraineeMutation] = useUnassignTraineeMutation();
  const [editTrainingMutation] = useEditTrainingMutation();

  // Read from legacy Redux state, but fall back to URL path segments (RTK Query
  // migration means the legacy studentData slice may not be populated).
  // useParams() is unavailable here because the Actions bar renders outside the
  // <Route> element tree, so we parse the pathname directly.
  const { term: reduxTerm, applicantEmployeeId: reduxApplicantId, committeeId: reduxCommitteeId } = useSelector(state => {
    const { demographics } = getStudentState(state)?.data ?? {}
    return {
      term: demographics?.admitTerm?.key,
      applicantEmployeeId: demographics?.applicantEmployeeId,
      committeeId: demographics?.committeeId,
    }
  })

  // URL pattern: /applicant/:applicantId/:committeeId/:admitTerm  or  /training/:applicantId/:committeeId/:admitTerm
  const pathSegments = location?.pathname?.split('/').filter(Boolean) ?? [];
  const isApplicantOrTrainingPath = ['applicant', 'training'].includes(pathSegments[0]);
  const urlApplicantId = isApplicantOrTrainingPath ? pathSegments[1] : undefined;
  const urlCommitteeId = isApplicantOrTrainingPath ? pathSegments[2] : undefined;
  const urlAdmitTerm = isApplicantOrTrainingPath ? pathSegments[3] : undefined;

  const term = reduxTerm || urlAdmitTerm;
  const applicantEmployeeId = reduxApplicantId || urlApplicantId;
  const committeeId = reduxCommitteeId || urlCommitteeId;

  const trainingState = useSelector((state) => getTrainingState(state));
  const editTrainingState = useSelector((state) => getEditTrainingState(state));
  const unassignTrainingState = useSelector((state) => getUnassignTraineeState(state));
  
  // Helper to dispatch slice action creators by dialog type
  const dispatchByType = (actionType, payload) => {
    const actionCreatorMap = {
      [SAVE_REVIEW]: saveReviewAction,
      [ASSIGN_APPLICATION]: assignApplicationAction,
      [UNASSIGN_APPLICATION]: unassignApplicationAction,
      [REASSIGN_APPLICATION]: reassignApplicationAction,
      [REPORT_CONFLICT]: reportConflictAction,
      [RESTART_APPLICATION]: restartApplicationAction,
      [NOTIFY_ADMIN]: notifyAdminAction,
      [CLEAR_NOTIFY_ADMIN]: clearNotifyAdminAction,
      [CREATE_TRAINING]: createTrainingAction,
      [UNASSIGN_TRAINEE]: unassignTraineeAction,
      [EDIT_TRAINING]: editTrainingAction,
      [UPDATE_PRIORITY]: updatePriorityAction,
    };
    const creator = actionCreatorMap[actionType];
    if (creator) {
      dispatch(creator(payload));
    }
  };

  let actions = [
    {
      label: "Cancel",
      id: "action-button-cancel",
      onClick: () => dialogState?.type === OVERRIDE_RECOMMENDATION || !dialogState ? onClose() :
        dispatchByType(dialogState?.type, { ...dialogState, status: STATUS?.CANCEL, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT }),
      color: "secondary",
      variant: "outlined",
      border: 'none',
    },
  ];

  const handleAssignApplication = () => {
    if (reviewer === "") {
      dispatch(assignApplicationAction({
        alert: ERROR_CONSTS.UI.GENERIC.SELECT_REVIEWER_EMPTY
      }));
    } else if (!isValidFutureDate(completedByDate)) {
      dispatch(assignApplicationAction({
        alert: ERROR_CONSTS.UI.GENERIC.SELECT_COMPLETEDBYDATE_EMPTY
      }));
    } else {
      dispatchByType(ASSIGN_APPLICATION, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
      assignApplicationMutation({
        applicantEmployeeId,
        committeeId,
        term,
        reviewerEmployeeId: reviewer.emplid,
        reviewerFirstName: reviewer.firstName,
        reviewerLastName: reviewer.lastName,
        reviewerCompletedByDueDate: completedByDate,
      }).unwrap().then((result) => {
        if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
          dispatchByType(ASSIGN_APPLICATION, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Assigned successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
        } else {
          dispatchByType(ASSIGN_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
        }
      }).catch((error) => {
        dispatchByType(ASSIGN_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      });
    }
  }

  const handleUnassignTrainee = () => {
    dispatchByType(UNASSIGN_TRAINEE, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    unassignTraineeMutation({
      applicantEmployeeId,
      committeeId,
      term,
      emplids: unassignTrainingState?.emplids,
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(UNASSIGN_TRAINEE, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Unassigned successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(UNASSIGN_TRAINEE, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(UNASSIGN_TRAINEE, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }

  const handleCreateTraining = () => {
    if (isApplicantPage && !trainingState?.expectedRecommendation) {
      dispatch(createTrainingAction({
        ...trainingState,
        status: STATUS?.ERROR,
        alert: {
          MESSAGE: 'Please select an expected recommendation before submitting.',
          SEVERITY: SEVERITY_LEVELS?.WARNING,
        }, 
      }));
    } else if (!trainingState?.allReviewers && trainingState?.emplids?.length === 0) {
      dispatch(createTrainingAction({
        ...trainingState,
        status: STATUS?.ERROR,
        alert: {
          MESSAGE: 'Please select one or more reviewers before submitting.',
          SEVERITY: SEVERITY_LEVELS?.WARNING,
        }, 
      }));
    } else {
      dispatchByType(CREATE_TRAINING, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
      createTrainingMutation({
        applicantEmployeeId,
        committeeId,
        term,
        expectedRecommendation: trainingState?.expectedRecommendation,
        emplids: trainingState?.emplids,
      }).unwrap().then((result) => {
        if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
          dispatchByType(CREATE_TRAINING, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Training created successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
        } else {
          dispatchByType(CREATE_TRAINING, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
        }
      }).catch((error) => {
        dispatchByType(CREATE_TRAINING, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      });
    }
  }

  const handleUnassignApplication = () => {
    if (reviewer === "") {
      dispatch(unassignApplicationAction({
        alert: ERROR_CONSTS.UI.GENERIC.SELECT_REVIEWER_EMPTY
      }));
      return;
    }
    dispatchByType(UNASSIGN_APPLICATION, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    unassignApplicationMutation({
      applicantEmployeeId,
      committeeId,
      term,
      reviewerEmployeeId: reviewer.emplid,
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(UNASSIGN_APPLICATION, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Unassigned successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(UNASSIGN_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(UNASSIGN_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }

  const handleUpdatePriority = () => {
    dispatchByType(UPDATE_PRIORITY, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    updatePriorityMutation({
      applicantEmployeeId,
      committeeId,
      term,
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(UPDATE_PRIORITY, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Priority updated successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(UPDATE_PRIORITY, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(UPDATE_PRIORITY, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }

  const handleEditTraining = () => {
    dispatchByType(EDIT_TRAINING, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    editTrainingMutation({
      applicantEmployeeId,
      committeeId,
      term,
      expectedRecommendation: editTrainingState?.expectedRecommendation,
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(EDIT_TRAINING, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Training updated successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(EDIT_TRAINING, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(EDIT_TRAINING, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }
  
  const reportAppConflict = () => {
    dispatchByType(REPORT_CONFLICT, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    reportConflictMutation({
      applicantEmployeeId,
      committeeId,
      term,
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(REPORT_CONFLICT, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Conflict reported successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(REPORT_CONFLICT, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(REPORT_CONFLICT, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }

  const restartEval = () => {
    dispatchByType(RESTART_APPLICATION, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    restartEvaluationMutation({
      applicantEmployeeId,
      committeeId,
      term
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(RESTART_APPLICATION, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Evaluation restarted successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(RESTART_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(RESTART_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }

  const handleSaveReview = () => {
    const checkedCompetitive = exportStringArray(pageAppHeader?.noteState, "competitive");
    const checkedNoncompetitive = exportStringArray(pageAppHeader?.noteState, "noncompetitive");
    const checkedDocument = exportStringArray(pageAppHeader?.noteState, "document");
    const checkedEvaluation = exportStringArray(pageAppHeader?.noteState, "evaluation");
    const checkedOther = exportStringArray(pageAppHeader?.noteState, "othersConcerns");

    dispatchByType(SAVE_REVIEW, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    saveReviewMutation({
      admitTerm: term,
      applicantEmployeeId: applicantEmployeeId,
      committeeId: committeeId,
      reviewNotes: {
        competitive: checkedCompetitive,
        document: checkedDocument,
        evaluation: checkedEvaluation,
        noncompetitive: checkedNoncompetitive,
        othersConcerns: checkedOther
      },
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(SAVE_REVIEW, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Review saved successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(SAVE_REVIEW, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(SAVE_REVIEW, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }

  const handleNotifyAdmin = () => {
    if (data?.length <= 0) {
      dispatch(notifyAdminAction({
        alert: ERROR_CONSTS.UI.GENERIC.NOTIFY_EMPTY
      }));
    } else {
      const notes = data.map((item) => item.label);
      dispatchByType(NOTIFY_ADMIN, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
      notifyAdminMutation({
        applicantEmployeeId: applicantEmployeeId,
        committeeId: committeeId,
        term: term,
        notes: notes
      }).unwrap().then((result) => {
        if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
          dispatchByType(NOTIFY_ADMIN, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Admin notified successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
        } else {
          dispatchByType(NOTIFY_ADMIN, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
        }
      }).catch((error) => {
        dispatchByType(NOTIFY_ADMIN, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      });
    }
  }

  const handleClearNotifyAdmin = () => {
    dispatchByType(CLEAR_NOTIFY_ADMIN, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
    clearNotifyAdminMutation({
      applicantEmployeeId: applicantEmployeeId,
      committeeId: committeeId,
      term: term       
    }).unwrap().then((result) => {
      if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
        dispatchByType(CLEAR_NOTIFY_ADMIN, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Notification cleared successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
      } else {
        dispatchByType(CLEAR_NOTIFY_ADMIN, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      }
    }).catch((error) => {
      dispatchByType(CLEAR_NOTIFY_ADMIN, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
    });
  }

  const handleReassignApplication = () => {
    if (reviewer === "" || secondReviewer === "") {
      dispatch(reassignApplicationAction({
        alert: ERROR_CONSTS.UI.GENERIC.SELECT_REVIEWER_EMPTY
      }));
    } else if (!isValidFutureDate(completedByDate)) {
      dispatch(assignApplicationAction({
        alert: ERROR_CONSTS.UI.GENERIC.SELECT_COMPLETEDBYDATE_EMPTY
      }));
    } else {
      dispatchByType(REASSIGN_APPLICATION, { status: STATUS.LOADING, alert: ERROR_CONSTS.UI.GENERIC.EMPTY_ALERT });
      reassignApplicationMutation({
        applicantEmployeeId,
        committeeId,
        term,
        reviewerEmployeeIdFrom: reviewer.emplid,
        reviewerEmployeeIdTo: secondReviewer.emplid,
        reviewerCompletedByDueDate: completedByDate,
      }).unwrap().then((result) => {
        if (result?.status?.toUpperCase() === STATUS.SUCCESS) {
          dispatchByType(REASSIGN_APPLICATION, { status: STATUS.SUCCESS, alert: { MESSAGE: result?.details ?? 'Reassigned successfully', SEVERITY: SEVERITY_LEVELS.SUCCESS } });
        } else {
          dispatchByType(REASSIGN_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: result?.error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
        }
      }).catch((error) => {
        dispatchByType(REASSIGN_APPLICATION, { status: STATUS.ERROR, alert: { MESSAGE: error?.message ?? 'An error occurred', SEVERITY: SEVERITY_LEVELS.ERROR } });
      });
    }
  }

  switch (type) {
    case ASSIGN_APPLICATION:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Assigning" : "Assign",
        id: "action-button-assign",
        onClick: () => { handleAssignApplication() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case REPORT_CONFLICT:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Reporting" : "Report",
        id: "action-button-resume-later",
        onClick: () => { reportAppConflict() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case SAVE_REVIEW:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Saving" : "Save",
        id: "action-button-resume-later",
        onClick: () => { handleSaveReview() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case REASSIGN_APPLICATION:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Reassigning" : "Reassign",
        id: "action-button-reassign-reviewer",
        onClick: () => { handleReassignApplication() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case UNASSIGN_APPLICATION:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Unassigning" : "Unassign",
        id: "action-button-unassign-reviewer",
        onClick: () => { handleUnassignApplication() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case OVERRIDE_RECOMMENDATION:
      actions.push({
        label: "Unlock",
        id: "action-button-override-rec",
        onClick: () => { pageAppHeader.unlock(); onClose(); },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case RESTART_APPLICATION:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Restarting" : "Restart",
        id: "action-button-restart-eval",
        onClick: () => { restartEval(); },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case NOTIFY_ADMIN:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Sending" : "Send",
        disabled: dialogState?.disabled || dialogState?.status === STATUS.LOADING,
        id: "action-button-notify-admin",
        onClick: () => { handleNotifyAdmin() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case CLEAR_NOTIFY_ADMIN:
        actions.push({
          label: dialogState?.status === STATUS?.LOADING ? "Clearing" : "Clear",
          id: "action-button-clear-notify-admin",
          onClick: () => { handleClearNotifyAdmin() },
          color: "primary",
          variant: "contained",
          border: 'none',
        });
        break;
    case CREATE_TRAINING:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Creating..." : "Create",
        id: "action-button-create-training",
        onClick: () => { handleCreateTraining() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case ASSIGN_TRAINEE:
        actions.push({
          label: dialogState?.status === STATUS?.LOADING ? "Assigning..." : "Assign",
          id: "action-button-assign-trainee",
          onClick: () => { handleCreateTraining() },
          color: "primary",
          variant: "contained",
          border: 'none',
        });
        break;
    case UNASSIGN_TRAINEE:
      actions.push({
        label: dialogState?.status === STATUS?.LOADING ? "Unassigning..." : "Unassign",
        id: "action-button-unassign-training",
        onClick: () => { handleUnassignTrainee() },
        color: "primary",
        variant: "contained",
        border: 'none',
      });
      break;
    case UPDATE_PRIORITY:
          actions.push({
            label: dialogState?.status === STATUS?.LOADING ? "Updating..." : "Update",
            id: "action-button-update-priority",
            onClick: () => { handleUpdatePriority() },
            color: "primary",
            variant: "contained",
            border: 'none',
          });
      break;
    case EDIT_TRAINING:
          actions.push({
            label: dialogState?.status === STATUS?.LOADING ? "Updating..." : "Update",
            id: "action-button-edit-training",
            onClick: () => { handleEditTraining() },
            color: "primary",
            variant: "contained",
            border: 'none',
            disabled: editTrainingState?.previousExpectedRecommendation === editTrainingState?.expectedRecommendation
          });
          break;
    default:
      break;
  }

  return {
    actions
  }
};

export default useActionsDialog;
